import { getSkillContextToolTitle } from '@apps/web/components/phoebe_chat_view/tool_calls/skill_context_tool_ui';
import { getConfiguredToolDisplayName } from '@apps/web/components/phoebe_chat_view/tool_calls/tool_call_renderers';

// Keep this map aligned with _TOOL_STATUS_TITLE_ARGUMENTS in
// libraries/python/agent_event_framework/persistence.py.
export const GENERIC_PURPOSE_TITLE_TOOL_ARGUMENTS: Record<
    string,
    ReadonlyArray<string>
> = {
    diff_run_data: ['title', 'purpose'],
    query: ['description'],
    run_bash: ['description'],
    read_run_data: ['title', 'purpose'],
    search_run_data: ['title', 'purpose'],
    slice_json: ['title', 'purpose'],
};

function getOptionalString(value: unknown): string | null {
    if (typeof value !== 'string') {
        return null;
    }
    const normalized = value.trim();
    return normalized.length > 0 ? normalized : null;
}

export function getGenericPurposeToolTitle({
    toolName,
    args,
}: {
    readonly toolName: string;
    readonly args?: Record<string, unknown> | Array<unknown> | null;
}): string | null {
    const argumentNames = GENERIC_PURPOSE_TITLE_TOOL_ARGUMENTS[toolName];
    if (
        argumentNames === undefined ||
        Array.isArray(args) ||
        args === null ||
        args === undefined
    ) {
        return null;
    }

    for (const argumentName of argumentNames) {
        const value = getOptionalString(args[argumentName]);
        if (value !== null) {
            return value;
        }
    }
    return null;
}

function shouldDoubleFinalConsonant(word: string): boolean {
    const lower = word.toLowerCase();
    const vowels = 'aeiou';
    if (lower.length < 3 || lower.length > 4) {
        return false;
    }
    const last = lower.charAt(lower.length - 1);
    const secondLast = lower.charAt(lower.length - 2);
    const thirdLast = lower.length >= 4 ? lower.charAt(lower.length - 3) : null;
    return (
        !vowels.includes(last) &&
        !'wxy'.includes(last) &&
        vowels.includes(secondLast) &&
        (thirdLast === null || !vowels.includes(thirdLast)) &&
        (lower.length === 3 || !vowels.includes(lower.charAt(0)))
    );
}

const CANONICAL_SERVICE_DISPLAY_NAMES: Record<string, string> = {
    braintrust: 'Braintrust',
    calendar: 'Calendar',
    datadog: 'Datadog',
    github: 'GitHub',
    google: 'Google',
    granola: 'Granola',
    linear: 'Linear',
    logfire: 'Logfire',
    notion: 'Notion',
    posthog: 'PostHog',
    ringcentral: 'RingCentral',
    sentry: 'Sentry',
    slack: 'Slack',
    snowflake: 'Snowflake',
    temporal: 'Temporal',
    twilio: 'Twilio',
};

const CANONICAL_SERVICE_PHRASES: ReadonlyArray<{
    readonly words: ReadonlyArray<string>;
    readonly displayName: string;
}> = [
    { words: ['git', 'hub'], displayName: 'GitHub' },
    { words: ['google', 'calendar'], displayName: 'Google Calendar' },
    { words: ['post', 'hog'], displayName: 'PostHog' },
    { words: ['ring', 'central'], displayName: 'RingCentral' },
];

function canonicalServiceMatch(
    words: ReadonlyArray<string>,
    index: number,
): { readonly displayName: string; readonly wordCount: number } | null {
    for (const phrase of CANONICAL_SERVICE_PHRASES) {
        const matches = phrase.words.every(
            (word, offset) => words[index + offset]?.toLowerCase() === word,
        );
        if (matches) {
            return {
                displayName: phrase.displayName,
                wordCount: phrase.words.length,
            };
        }
    }

    const displayName =
        CANONICAL_SERVICE_DISPLAY_NAMES[words[index]?.toLowerCase() ?? ''];
    return displayName ? { displayName, wordCount: 1 } : null;
}

type FallbackWordCasing = 'preserve' | 'sentence';

function formatFallbackWord({
    fallbackCasing,
    isFirstWord,
    word,
}: {
    readonly fallbackCasing: FallbackWordCasing;
    readonly isFirstWord: boolean;
    readonly word: string;
}): string {
    if (fallbackCasing === 'preserve') {
        return word;
    }

    return isFirstWord
        ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        : word.toLowerCase();
}

function formatServiceAwareWords(
    words: ReadonlyArray<string>,
    fallbackCasing: FallbackWordCasing = 'sentence',
): string {
    const formattedWords: Array<string> = [];
    let index = 0;
    while (index < words.length) {
        const word = words[index]?.trim();
        if (!word) {
            index += 1;
            continue;
        }

        const canonicalService = canonicalServiceMatch(words, index);
        if (canonicalService) {
            formattedWords.push(canonicalService.displayName);
            index += canonicalService.wordCount;
            continue;
        }

        formattedWords.push(
            formatFallbackWord({
                fallbackCasing,
                isFirstWord: formattedWords.length === 0,
                word,
            }),
        );
        index += 1;
    }
    return formattedWords.join(' ');
}

export function formatCanonicalServiceLabel(value: string): string {
    const words = value
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .split(/[_\-\s]+/)
        .filter(Boolean);
    const label = formatServiceAwareWords(
        words,
        /[A-Z]/.test(value) ? 'preserve' : 'sentence',
    );
    return label || value;
}

/** Add "-ing" suffix applying standard English conjugation rules. */
export function addIngSuffix(word: string): string {
    const lower = word.toLowerCase();
    // ie -> ying: tie -> tying
    if (lower.endsWith('ie')) {
        return word.slice(0, -2) + 'ying';
    }
    // y -> ying after consonants: notify -> notifying, but play -> playing
    if (
        lower.endsWith('y') &&
        word.length > 1 &&
        !'aeiou'.includes(lower.charAt(lower.length - 2))
    ) {
        return word.slice(0, -1) + 'ying';
    }
    // Drop silent e: save -> saving, create -> creating
    // Keep ee (see -> seeing), ye (dye -> dyeing), oe (toe -> toeing)
    if (
        lower.endsWith('e') &&
        !lower.endsWith('ee') &&
        !lower.endsWith('ye') &&
        !lower.endsWith('oe')
    ) {
        return word.slice(0, -1) + 'ing';
    }
    // Double final consonant for short CVC words: set -> setting, stop -> stopping
    // For 4-letter words, require consonant-initial (CCVC) to avoid doubling
    // two-syllable vowel-initial words like edit -> editing, open -> opening.
    if (shouldDoubleFinalConsonant(word)) {
        return word + word.charAt(word.length - 1) + 'ing';
    }
    return word + 'ing';
}

function addPastTenseSuffix(word: string): string {
    const lower = word.toLowerCase();
    // Common irregular verbs that may appear as the first word of a tool
    // name. Without this, "find_X" would render with a literal -ed suffix
    // instead of "Found X". Curated tool-step / display-name registry
    // entries bypass this helper, so this is a safety net for unmapped
    // tools.
    const irregularPastTense: Record<string, string> = {
        build: 'built',
        cut: 'cut',
        feed: 'fed',
        find: 'found',
        get: 'got',
        hold: 'held',
        keep: 'kept',
        leave: 'left',
        make: 'made',
        put: 'put',
        read: 'read',
        run: 'ran',
        say: 'said',
        seek: 'sought',
        sell: 'sold',
        send: 'sent',
        set: 'set',
        spend: 'spent',
        tell: 'told',
        think: 'thought',
        write: 'wrote',
    };
    const irregular = irregularPastTense[lower];
    if (irregular) {
        return word.charAt(0).toUpperCase() + irregular.slice(1);
    }
    if (
        lower.endsWith('y') &&
        word.length > 1 &&
        !'aeiou'.includes(lower.charAt(lower.length - 2))
    ) {
        return word.slice(0, -1) + 'ied';
    }
    if (lower.endsWith('e')) {
        return word + 'd';
    }
    if (shouldDoubleFinalConsonant(word)) {
        return word + word.charAt(word.length - 1) + 'ed';
    }
    return word + 'ed';
}

export function formatToolName(
    toolName: string,
    isComplete: boolean,
    args?: Record<string, unknown> | Array<unknown> | null,
    output?: unknown,
): string {
    const skillContextTitle = getSkillContextToolTitle({
        toolName,
        isComplete,
        args,
        output,
    });
    if (skillContextTitle) {
        return skillContextTitle;
    }

    const genericPurposeTitle = getGenericPurposeToolTitle({
        toolName,
        args,
    });
    if (genericPurposeTitle) {
        return genericPurposeTitle;
    }

    const displayName = getConfiguredToolDisplayName(toolName);
    if (displayName) {
        const base = isComplete ? displayName.complete : displayName.active;
        return base;
    }

    const rawWords = toolName.startsWith('get_')
        ? toolName.slice(4).split('_')
        : toolName.split('_');

    if (toolName.startsWith('get_')) {
        const prefix = isComplete ? 'Retrieved' : 'Retrieving';
        return formatServiceAwareWords([prefix, ...rawWords]);
    }

    if (isComplete) {
        if (toolName.endsWith('_search')) {
            return formatServiceAwareWords([
                'Searched',
                ...rawWords.slice(0, -1),
            ]);
        }
        const words = [...rawWords];
        if (words[0]) {
            words[0] = addPastTenseSuffix(
                words[0].charAt(0).toUpperCase() + words[0].slice(1),
            );
        }
        return formatServiceAwareWords(words);
    }

    if (toolName.endsWith('_search')) {
        return formatServiceAwareWords(['Searching', ...rawWords.slice(0, -1)]);
    }
    const words = [...rawWords];
    if (words[0]) {
        words[0] = addIngSuffix(
            words[0].charAt(0).toUpperCase() + words[0].slice(1),
        );
    }

    return formatServiceAwareWords(words);
}
